// SwipeRevealRow's two menus, as pure decisions.
//
// The same split as sheetGesture.js / announcementToastGesture.js: the
// component's handlers are plumbing — capture the pointer, ask these, apply the
// offset — and the guard matrix is a table of numbers in and verdicts out a
// test can ask directly. Born as Bid Stack's row menu (View, Remove).
//
// A row is swiped left to slide its face aside and uncover its trailing menu on
// the right. A row that also has a leading menu is swiped RIGHT to uncover that
// one on the left. A swipe the other way, or a tap on the face, slides it back.
//
// Offsets read from the face's resting place: negative is slid left (the
// trailing menu showing), positive is slid right (the leading menu showing).

/**
 * Whether every member of a menu is one the row can draw: a key, a label, a
 * PrimeIcons name, and a tone it knows (info | danger | neutral) or none. Here
 * rather than in the component because `defineProps` is hoisted out of setup and
 * can reach an import but not a local.
 */
export function isValidSwipeActions(actions) {
  return actions.every((action) => (
    typeof action?.key === 'string' && action.key.length > 0
    && typeof action?.label === 'string' && action.label.length > 0
    && typeof action?.icon === 'string'
    && (action.tone == null || ['info', 'danger', 'neutral'].includes(action.tone))
  ));
}

/**
 * Below this a press is not yet a gesture — the same floor Sheet and the toast
 * use, so a tap that jitters a pixel is still a tap.
 */
export const MOVEMENT_FLOOR_PX = 4;

/** Width of one menu action. */
export const ACTION_WIDTH_PX = 74;

/** How far the face slides for a menu of `count` actions. */
export function menuWidthFor(count) {
  return ACTION_WIDTH_PX * Math.max(1, count);
}

/** The two-action menu Bid Stack draws; the default every helper assumes. */
export const MENU_WIDTH_PX = menuWidthFor(2);

/**
 * Whether a press in progress is a row swipe, something else, or not yet
 * either — `'drag' | 'ignore' | 'wait'`, the house's three verdicts.
 *
 * - under the movement floor it is still a press, which may yet be a tap;
 * - a mostly-vertical gesture is the list scrolling, never a row swipe;
 * - a rightward drag on a closed row uncovers the leading menu when the row has
 *   one, and has nothing to uncover when it does not. On a row whose trailing
 *   menu is open it is how that menu is closed, so it is taken.
 *
 * @param {object} gesture
 * @param {number} gesture.deltaX  Horizontal travel since the pointer went down; negative is left.
 * @param {number} gesture.deltaY  Vertical travel since the pointer went down.
 * @param {boolean} [gesture.open] Whether the row's trailing menu was already open.
 * @param {boolean} [gesture.canLead] Whether the row has a leading menu to uncover.
 */
export function swipeVerdict({ deltaX, deltaY, open = false, canLead = false }) {
  const ax = Math.abs(deltaX);
  const ay = Math.abs(deltaY);
  if (ax < MOVEMENT_FLOOR_PX && ay < MOVEMENT_FLOOR_PX) return 'wait';
  if (ay >= ax) return 'ignore';
  if (deltaX > 0 && !open && !canLead) return 'ignore';
  return 'drag';
}

/**
 * Where the face sits for a drag of `deltaX` from its starting offset, held
 * between the trailing menu fully open (−menuWidth) and the leading one fully
 * open (+leadingWidth). A row with no leading menu stops at closed (0).
 */
export function dragOffset(startOffset, deltaX, menuWidth = MENU_WIDTH_PX, leadingWidth = 0) {
  return Math.min(leadingWidth, Math.max(-menuWidth, startOffset + deltaX));
}

/** Whether letting go at this offset leaves the trailing menu open, or slides the face back. */
export function opensOnRelease(offset, menuWidth = MENU_WIDTH_PX) {
  return offset <= -menuWidth / 2;
}

/**
 * Which menu letting go at this offset leaves open — `'trailing' | 'leading'` —
 * or null when the face slides back. Either menu stays open past half its width.
 */
export function openSideOnRelease(offset, menuWidth = MENU_WIDTH_PX, leadingWidth = 0) {
  if (opensOnRelease(offset, menuWidth)) return 'trailing';
  if (leadingWidth > 0 && offset >= leadingWidth / 2) return 'leading';
  return null;
}
